// ═══ LA BIOMETRÍA DEL CHECADOR: NUNCA BLOQUEA, Y SIEMPRE DICE POR QUÉ ═════════
//
// El caso que lo motivó: un trabajador de un cliente que checa todos los días no
// pudo checar. Su teléfono tenía huella, pero la llave que la app pedía no estaba
// en él (otra cuenta de Google, una llave borrada…), Android le ofrecía "usar otro
// dispositivo", y la app contestaba "Necesitas confirmar tu biometría… Intenta de
// nuevo." sin ninguna salida. Un fallo de biometría no puede dejar a nadie sin
// checar; lo que sí tiene que hacer es dejar escrito POR QUÉ esa checada no la
// lleva, para que el patrón lo vea y decida.
//
// Aquí vive lo que no depende de React ni de Firebase: reconocer un navegador
// interno, los motivos, la etiqueta que ve el patrón y la regla del
// "Restablecer biometría". Lo usa App.tsx y lo prueban las suites.

// ── Los motivos, escritos una vez ────────────────────────────────────────────
// Se guardan en la checada, en `motivoSinBiometria`, SOLO cuando
// `verificacionBiometrica` es false.
const MOTIVOS = {
  DISPOSITIVO: "dispositivo_sin_biometria",   // el teléfono (o su navegador) no ofrece biometría
  NAVEGADOR_INTERNO: "navegador_interno",     // abierta dentro de WhatsApp, Facebook, Instagram…
  FALLIDA: "biometria_fallida",               // tenía llave, la verificación falló o se canceló
  SIN_HUELLA: "sin_huella_registrada",        // no tenía llave y no se pudo registrar una
};

// ── Los navegadores internos ─────────────────────────────────────────────────
// Dentro de WhatsApp, Facebook, Instagram y parecidas la página no corre en
// Chrome sino en una vista incrustada, y ahí la huella (WebAuthn) casi nunca
// está. Se reconocen por el agente de usuario. El orden importa: Messenger se
// anuncia también como Facebook.
const INTERNOS = [
  ["WhatsApp", /WhatsApp/i],
  ["Messenger", /Messenger|FB_IAB\/MESSENGER|FBAN\/MessengerForiOS/i],
  ["Instagram", /Instagram/i],
  ["Facebook", /FBAN|FBAV|FB_IAB|FBIOS|\[FB/i],
  ["TikTok", /musical_ly|BytedanceWebview|TikTok/i],
  ["LinkedIn", /LinkedInApp/i],
  ["Snapchat", /Snapchat/i],
  ["Telegram", /Telegram/i],
  ["X (Twitter)", /Twitter/i],
  ["LINE", /\bLine\//],
];
// Devuelve el nombre de la app ("WhatsApp", "Facebook"…), "otra app" para una
// vista incrustada de Android sin nombre (`; wv)`), o null si es un navegador.
// ⚠️ UN BUCLE A LA ANTIGUA, A PROPÓSITO. Con `for (const [nombre, re] of …)` el
// build de la app (babel, para teléfonos viejos) inyecta un ayudante con un
// `import`, el archivo deja de ser CommonJS para webpack y sus `exports.X`
// desaparecen: "etiquetaBiometria is not exported". Sin desestructurar, no hay
// ayudante. `prueba-empaquetado` transforma cada módulo como el build y falla si
// alguno sale con un import.
function detectarNavegadorInterno(ua) {
  const s = String(ua || "");
  for (let i = 0; i < INTERNOS.length; i++) if (INTERNOS[i][1].test(s)) return INTERNOS[i][0];
  if (/;\s*wv\)/.test(s)) return "otra app";
  return null;
}

// ── Lo que ve el patrón ──────────────────────────────────────────────────────
// Antes decía "Sin biometría" para todo. Ahora dice por qué. `null` si la
// checada es anterior a que existiera el campo.
//
// LAS CHECADAS ANTIGUAS (false y sin motivo): hasta esta rama, si el teléfono
// tenía biometría y fallaba, NO se registraba nada; así que un false antiguo
// solo pudo salir de un teléfono o un navegador que no la ofrecía. Se dice eso,
// sin inventar cuál de los dos.
//
// LO QUE ES: UNA VERIFICACIÓN EN EL APARATO. La huella (o la cara) la comprueba
// el propio teléfono y nadie más; la plataforma no la verifica en el servidor.
// Por eso se dice "verificado en el aparato" y no "con biometría", y no se le
// atribuye ningún valor de prueba.
function etiquetaBiometria(c) {
  if (!c || c.verificacionBiometrica === undefined || c.verificacionBiometrica === null) return null;
  if (c.verificacionBiometrica === true) return { con: true, texto: "Verificado en el aparato" };
  return { con: false, texto: "Sin verificación en el aparato: " + motivoSinVerificacion(c) };
}
// POR QUÉ NO LA LLEVA, en palabras. La usan la etiqueta del patrón y la columna
// "Verificación en el aparato" de la bitácora.
function motivoSinVerificacion(c) {
  switch (c && c.motivoSinBiometria) {
    case MOTIVOS.DISPOSITIVO: return "el teléfono no la tiene";
    case MOTIVOS.NAVEGADOR_INTERNO: return "abierta desde " + (c.navegadorInterno && c.navegadorInterno !== "otra app" ? c.navegadorInterno : "otra app") + ", no desde Chrome";
    case MOTIVOS.FALLIDA: return "falló la verificación";
    // `sin_huella_registrada`: el teléfono no tenía llave de verificación en ese
    // navegador (primer uso, o recién restablecida) y no se pudo crear una (falló
    // o se canceló). La plataforma no sabe si iba a ser huella, rostro o PIN: se
    // dice solo lo que se sabe.
    case MOTIVOS.SIN_HUELLA: return "no se pudo activar en el teléfono";
    default: return "el teléfono o el navegador no la tenían";
  }
}

// ── "Restablecer biometría" ──────────────────────────────────────────────────
// El patrón escribe `bioRestablecidaEn` en la cuenta del trabajador. El teléfono
// compara esa fecha con la de SU llave: si la llave es más vieja, la olvida, y la
// siguiente checada registra una nueva (con el bloqueo del propio teléfono). Una
// llave sin fecha —de antes de esta rama— cuenta como más vieja que cualquier
// restablecimiento.
function llaveAnteriorAlRestablecimiento(restablecidaMs, llaveCreadaMs) {
  const r = Number(restablecidaMs) || 0;
  if (!r) return false;                       // nunca se restableció
  const k = Number(llaveCreadaMs) || 0;
  return k < r;
}

// CommonJS a propósito y exportado nombre por nombre: ver el porqué en `checador.js`.
exports.MOTIVOS = MOTIVOS;
exports.detectarNavegadorInterno = detectarNavegadorInterno;
exports.etiquetaBiometria = etiquetaBiometria;
exports.motivoSinVerificacion = motivoSinVerificacion;
exports.llaveAnteriorAlRestablecimiento = llaveAnteriorAlRestablecimiento;
